import { WebGLRenderer, ImageUtils, PerspectiveCamera, AxesHelper, Scene, RGBFormat, LinearMipmapLinearFilter, sRGBEncoding, BufferAttribute } from 'three';
import { PCFSoftShadowMap, WebGLCubeRenderTarget, CubeCamera } from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { GLTFExporter } from 'three/examples/jsm/exporters/GLTFExporter';

import { EVENT_UPDATED, EVENT_LOADED, EVENT_ITEM_SELECTED, EVENT_ITEM_MOVE, EVENT_ITEM_MOVE_FINISH, EVENT_NO_ITEM_SELECTED, EVENT_WALL_CLICKED, EVENT_ROOM_CLICKED, EVENT_GLTF_READY, EVENT_NEW_ITEM, EVENT_NEW_ROOMS_ADDED, EVENT_MODE_RESET, EVENT_EXTERNAL_FLOORPLAN_LOADED } from '../core/events.js';
// import { EVENT_NEW, EVENT_DELETED } from '../core/events.js';

import { Skybox } from './skybox.js';
import { Edge3D } from './edge3d.js';
import { Floor3D } from './floor3d.js';
import { Lights3D } from './lights3d.js';
import { Physical3DItem } from './Physical3DItem.js';
import { DragRoomItemsControl3D } from './DragRoomItemsControl3D.js';
import { Configuration, viewBounds } from '../core/configuration.js';
import { BoundaryView3D } from './BoundaryView3D.js';
import { Vector3 } from 'three/build/three.module';
import { Matrix4 } from 'three';
import { DoubleSide } from 'three';
import Wall from '../model/wall.js';
import { Mesh } from 'three';
import { MeshBasicMaterial } from 'three';
import { Material } from 'three';
import { Group } from 'three';
import { BufferGeometry } from 'three';
import { Buffer } from 'pixi.js';
import { BackSide } from 'three';
import { FrontSide } from 'three';

export class Viewer3D extends Scene {
    constructor(model, element, opts) {
        super();
        let options = { 
            occludedRoofs: false, 
            occludedWalls: false, 
            resize: true, 
            pushHref: false, 
            spin: true, 
            spinSpeed: .00002, 
            clickPan: true, 
            canMoveFixedItems: false };
        for (let opt in options) {
            if (options.hasOwnProperty(opt) && opts.hasOwnProperty(opt)) {
                options[opt] = opts[opt];
            }
        }

        // console.log('VIEWER 3D ::: ', options);

        this.__physicalRoomItems = [];
        this.__enabled = true;
        this.model = model;
        this.floorplan = this.model.floorplan;
        this.__options = options;

        this.domElement = document.getElementById(element);

        this.perspectivecamera = null;
        this.camera = null;
        this.__environmentCamera = null;

        this.cameraNear = 10;
        this.cameraFar = 100000;

        this.controls = null;

        this.renderer = null;
        this.controller = null;

        this.needsUpdate = false;
        this.lastRender = Date.now();

        this.heightMargin = null;
        this.widthMargin = null;
        this.elementHeight = null;
        this.elementWidth = null;
        this.pauseRender = false;

        this.edges3d = [];
        this.floors3d = [];

        this.__externalEdges3d = [];
        this.__externalFloors3d = [];

        this.__boundaryRegion3D = null;
        this.__currentItemSelected = null;

        this.needsUpdate = true;

        this.__newItemEvent = this.__addNewItem.bind(this);
        this.__wallSelectedEvent = this.__wallSelected.bind(this);
        this.__roomSelectedEvent = this.__roomSelected.bind(this);
        this.__roomItemSelectedEvent = this.__roomItemSelected.bind(this);
        this.__roomItemUnselectedEvent = this.__roomItemUnselected.bind(this);
        this.__roomItemDraggedEvent = this.__roomItemDragged.bind(this);
        this.__roomItemDragFinishEvent = this.__roomItemDragFinish.bind(this);

        this.__resetDesignEvent = this.__resetDesign.bind(this);

        this.init();
    }

    init() {
        let scope = this;

        ImageUtils.crossOrigin = '';

        scope.camera = new PerspectiveCamera(45, 10, scope.cameraNear, scope.cameraFar);

        let cubeRenderTarget = new WebGLCubeRenderTarget(16, { format: RGBFormat, generateMipmaps: true, minFilter: LinearMipmapLinearFilter });
        scope.__environmentCamera = new CubeCamera(1, 100000, cubeRenderTarget);
        scope.__environmentCamera.renderTarget.texture.encoding = sRGBEncoding;

        scope.renderer = scope.getARenderer();
        scope.domElement.appendChild(scope.renderer.domElement);

        scope.lights = new Lights3D(this, scope.floorplan);
        // scope.dragcontrols = new DragControls(this.physicalRoomItems, scope.camera, scope.renderer.domElement);
        scope.dragcontrols = new DragRoomItemsControl3D(this.floorplan.wallPlanesForIntersection, this.floorplan.floorPlanesForIntersection, this.physicalRoomItems, scope.camera, scope.renderer.domElement);
        scope.controls = new OrbitControls(scope.camera, scope.domElement);
        // scope.controls.autoRotate = this.__options['spin'];
        scope.controls.enableDamping = false;
        scope.controls.dampingFactor = 0.1;
        scope.controls.maxPolarAngle = Math.PI * 0.5; //Math.PI * 0.35;//Math.PI * 1.0; //
        scope.controls.maxDistance = Configuration.getNumericValue(viewBounds);// 7500; //2500
        scope.controls.minDistance = 100; //1000; //1000
        scope.controls.screenSpacePanning = true;

        scope.skybox = new Skybox(this, scope.renderer);
        scope.camera.position.set(0, 600, 1500);
        scope.controls.update();

        scope.axes = new AxesHelper(500);


        // handle window resizing
        scope.updateWindowSize();

        if (scope.__options.resize) {
            window.addEventListener('resize', () => { scope.updateWindowSize(); });
            window.addEventListener('orientationchange', () => { scope.updateWindowSize(); });
        }

        scope.model.addEventListener(EVENT_NEW_ITEM, scope.__newItemEvent);
        scope.model.addEventListener(EVENT_MODE_RESET, scope.__resetDesignEvent);
        // scope.model.addEventListener(EVENT_LOADED, (evt) => scope.addRoomItems(evt));
        // scope.floorplan.addEventListener(EVENT_UPDATED, (evt) => scope.addWalls(evt));

        scope.model.addEventListener(EVENT_LOADED, scope.addRoomItems.bind(scope));

        // scope.floorplan.addEventListener(EVENT_UPDATED, scope.addRoomsAndWalls.bind(scope));
        scope.floorplan.addEventListener(EVENT_NEW_ROOMS_ADDED, scope.addRoomsAndWalls.bind(scope));
        scope.floorplan.addEventListener(EVENT_EXTERNAL_FLOORPLAN_LOADED, scope.addExternalRoomsAndWalls.bind(scope));

        this.controls.addEventListener('change', () => { scope.needsUpdate = true; });


        scope.dragcontrols.addEventListener(EVENT_ITEM_SELECTED, this.__roomItemSelectedEvent);
        scope.dragcontrols.addEventListener(EVENT_ITEM_MOVE, this.__roomItemDraggedEvent);
        scope.dragcontrols.addEventListener(EVENT_ITEM_MOVE_FINISH, this.__roomItemDragFinishEvent);
        scope.dragcontrols.addEventListener(EVENT_NO_ITEM_SELECTED, this.__roomItemUnselectedEvent);

        scope.dragcontrols.addEventListener(EVENT_WALL_CLICKED, this.__wallSelectedEvent);
        scope.dragcontrols.addEventListener(EVENT_ROOM_CLICKED, this.__roomSelectedEvent);
        // scope.controls.enabled = false;//To test the drag controls

        //SEt the animation loop
        scope.renderer.setAnimationLoop(scope.render.bind(this));
        scope.render();
    }

    __wallSelected(evt) {
        this.dispatchEvent(evt);
    }

    __roomSelected(evt) {
        this.dispatchEvent(evt);
    }

    __roomItemSelected(evt) {
        if (this.__currentItemSelected) {
            this.__currentItemSelected.selected = false;
        }
        this.__currentItemSelected = evt.item;
        this.__currentItemSelected.selected = true;
        this.needsUpdate = true;
        evt.itemModel = this.__currentItemSelected.itemModel;
        this.dispatchEvent(evt);
    }

    __roomItemDragged(evt) {
        this.controls.enabled = false;
        this.needsUpdate = true;
    }

    __roomItemDragFinish(evt) {
        this.controls.enabled = true;
    }

    __roomItemUnselected(evt) {
        this.controls.enabled = true;
        if (this.__currentItemSelected) {
            this.__currentItemSelected.selected = false;
            this.__currentItemSelected = null;
            this.needsUpdate = true;
        }
        this.dispatchEvent(evt);
    }

    __addNewItem(evt) {
        if (!evt.item) {
            return;
        }
        let physicalRoomItem = new Physical3DItem(evt.item, this.__options);
        physicalRoomItem.name = "object"
        this.add(physicalRoomItem);
        this.__physicalRoomItems.push(physicalRoomItem);
        // only create parametric context gui if necessary
        evt.gui ? this.__roomItemSelected({ type: EVENT_ITEM_SELECTED, item: physicalRoomItem }) : null;
    }

    __resetDesign(evt) {
        this.addRoomItems();
        this.addRoomsAndWalls();
        this.addExternalRoomsAndWalls();
    }

    deleteItem(item, deleteWall = true) {//item is a Physical3DItem (like door or window)
        let item_uuid = item.uuid;//the id of the physical 3d item
        let id = item.__itemModel.id;// the id of the item model (inWallFloorItem for example)
        for (let index = 0; index < this.__physicalRoomItems.length; index++) {
            if (this.__physicalRoomItems[index].uuid === item_uuid) {
                this.__physicalRoomItems[index].dispose();
                this.remove(this.__physicalRoomItems[index]);
                this.__physicalRoomItems.splice(index, 1);
            }
        }
        for (let index = 0; index < this.model.roomItems.length; index++) {
            if (this.model.roomItems[index].id === id) {
                this.model.roomItems.splice(index, 1);
            }
        }
        if (deleteWall) {
            let current_wall = item.__itemModel.__currentWall;
            let in_wall_items = item.__itemModel.__currentWall.__inWallItems;
            // let on_wall_items TODO, for now on_wall_items is not included since the items are double
            item.__itemModel.__currentWall.remove();
            this.floorplan.newWall(current_wall.start, current_wall.end, null, null, current_wall.frontTexture, current_wall.backTexture);
            for (let index = 0; index < this.floorplan.walls.length; index++) {
                if (this.floorplan.walls[index].id === current_wall.id) {
                    this.floorplan.walls[index].thickness = current_wall.thickness;
                    in_wall_items.forEach( (itm) => {
                        if (itm.id !== id) {
                            this.floorplan.walls[index].addItem(itm);
                        }
                    })
                }    
            }
        }
    }

    addRoomItems(evt) {
        let i = 0;
        for (; i < this.__physicalRoomItems.length; i++) {
            this.__physicalRoomItems[i].dispose();
            this.remove(this.__physicalRoomItems[i]);
        }
        this.__physicalRoomItems.length = 0; //A cool way to clear an array in javascript
        let roomItems = this.model.roomItems;
        for (i = 0; i < roomItems.length; i++) {
            let physicalRoomItem = new Physical3DItem(roomItems[i], this.__options);
            physicalRoomItem.name = "object"
            this.add(physicalRoomItem);
            this.__physicalRoomItems.push(physicalRoomItem);
        }

    }


    __drawBoundary(){
        if(this.__boundaryRegion3D){
            this.__boundaryRegion3D.removeFromScene();
        }

        if(this.floorplan.boundary){
            if(this.floorplan.boundary.isValid){
                this.__boundaryRegion3D = new BoundaryView3D(this, this.floorplan, this.__options, this.floorplan.boundary);
            }            
        }
    }

    addRoomsAndWalls() {
        let scope = this;
        let i = 0;

        // clear scene
        scope.floors3d.forEach((floor) => {
            floor.destroy();
            floor = null;
        });

        scope.edges3d.forEach((edge3d) => {
            edge3d.remove();
            edge3d = null;
        });

        scope.edges3d = [];
        scope.floors3d = [];
        let wallEdges = scope.floorplan.wallEdges();
        let rooms = scope.floorplan.getRooms();

        this.__drawBoundary();

        // draw floors
        for (i = 0; i < rooms.length; i++) {
            var threeFloor = new Floor3D(scope, rooms[i], scope.controls, this.__options);
            scope.floors3d.push(threeFloor);
        }

        for (i = 0; i < wallEdges.length; i++) {
            let edge3d = new Edge3D(scope, wallEdges[i], scope.controls, this.__options);
            scope.edges3d.push(edge3d);
        }

        scope.shouldRender = true;

        let floorplanCenter = scope.floorplan.getDimensions(true);
        scope.controls.target = floorplanCenter.clone();
        scope.camera.position.set(floorplanCenter.x, 300, floorplanCenter.z * 5);
        scope.controls.update();
    }


    addExternalRoomsAndWalls() {
        // console.trace('ADD EXTERNAL ROOMS AND WALLS');
        let scope = this;
        let i = 0;

        // clear scene
        scope.__externalFloors3d.forEach((floor) => {
            floor.destroy();
            floor = null;
        });

        scope.__externalEdges3d.forEach((edge3d) => {
            edge3d.remove();
            edge3d = null;
        });

        scope.__externalEdges3d = [];
        scope.__externalFloors3d = [];

        let wallEdges = scope.floorplan.externalWallEdges();
        let rooms = scope.floorplan.externalRooms;

        this.__drawBoundary();

        // draw floors
        for (i = 0; i < rooms.length; i++) {
            var threeFloor = new Floor3D(scope, rooms[i], scope.controls, this.__options);
            scope.__externalFloors3d.push(threeFloor);
        }

        for (i = 0; i < wallEdges.length; i++) {
            let edge3d = new Edge3D(scope, wallEdges[i], scope.controls, this.__options);
            scope.__externalEdges3d.push(edge3d);
        }

        scope.shouldRender = true;

        let floorplanCenter = scope.floorplan.getDimensions(true);
        scope.controls.target = floorplanCenter.clone();
        scope.camera.position.set(floorplanCenter.x, 300, floorplanCenter.z * 5);
        scope.controls.update();
    }

    getARenderer() {
        var renderer = new WebGLRenderer({ antialias: true, alpha: true });

        // scope.renderer.autoClear = false;
        renderer.shadowMap.enabled = false;
        renderer.shadowMapSoft = true;
        renderer.shadowMap.type = PCFSoftShadowMap;
        renderer.setClearColor(0xFFFFFF, 1);
        renderer.localClippingEnabled = false;
        // renderer.gammaOutput = false;
        renderer.outputEncoding = sRGBEncoding;
        renderer.setPixelRatio(window.devicePixelRatio);
        // renderer.sortObjects = false;
        return renderer;
    }

    updateWindowSize() {
        let heightMargin = this.domElement.offsetTop;
        let widthMargin = this.domElement.offsetLeft;
        let elementWidth = (this.__options.resize) ? window.innerWidth - widthMargin : this.domElement.clientWidth;
        let elementHeight = (this.__options.resize) ? window.innerHeight - heightMargin : this.domElement.clientHeight;

        this.camera.aspect = elementWidth / elementHeight;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(elementWidth, elementHeight);
        this.needsUpdate = true;
    }

    render() {
        if (!this.enabled) {
            return;
        }
        let scope = this;
        // scope.controls.update();
        if (!scope.needsUpdate) {
            return;
        }
        scope.renderer.render(scope, scope.camera);
        scope.lastRender = Date.now();
        this.needsUpdate = false;
    }

    //export the scene as glb,
    //if sendToHegias is true it will dispatch the event to send the glb to HEGIAS VR
    //if returnScene is true, the scene will be returned
    exportSceneAsGTLF(sendToHegias = false, returnScene = false, orgId = 0, projectId = 0) {
        let scope = this;
        let exporter = new GLTFExporter();
        let m = new Matrix4();//this matrix wil scale the geometry 100 times smaller
        m.set(0.01, 0,    0,    0,
              0,    0.01, 0,    0,
              0,    0,    0.01, 0,
              0,    0,    0,    1);
        let new_scene = new Scene()//create a new scene (this scene will be exported)
        for (let index = 0; index < this.children.length; index++) {
            if (this.children[index].name === "wall") {//walls are converted to buffergeometry
                let child = this.children[index].clone();//copy the mesh
                child.material.name= "White Concrete 13";
                let newChild = new Mesh( new BufferGeometry().fromGeometry(child.geometry.clone()), child.material)//convert from shape geometry to buffergeometry
                newChild.name = "wall"
                newChild.applyMatrix4(m)//rescale it
                new_scene.add(newChild);//ad to the new scene
            } else if (this.children[index].name === "floor") {//the floor only need rescaling
                let child = this.children[index].clone();//copy the mesh
                child.material.name = "Eiche rustikal weiss";
                child.applyMatrix4(m)//rescale it
                new_scene.add(child);//ad to the new scene
            } else if (this.children[index].name === "ceiling") {//the roof also need the visibility inverted
                let child = this.children[index].clone();//copy the mesh
                let newChild = new Mesh(child.geometry.clone(), child.material)//convert from shape geometry to buffergeometry
                newChild.name = "ceiling";
                newChild.material.side = DoubleSide;
                newChild.material.name = "White Concrete 12";
                newChild.applyMatrix4(m)
                new_scene.add(newChild)
                this.children[index].material.visible = false;//DoubleSide cause the ceiling to become visible also in the 3dViewer after the export this is to prevent it from appearing in the 3d view
            } else if (this.children[index].name === "object") {
                let child = this.children[index];
                const position = child.position;//save the position of the object
                let real_mesh;//we only need the model of the object and not the other helper objects
                if (this.children[index].__itemModel.itemName === "window") {
                    for (let obj of child.children) {
                        if (obj.type === "Mesh") {//find the right one
                            let newMeshes = this.createNewWindow(obj.clone())
                            let glassMesh = newMeshes.glassMesh;
                            let frameMesh = newMeshes.frameMesh;
                            
                            glassMesh.name = "glass";
                            glassMesh.position.set(position.x, position.y, position.z)//set the original position
                            glassMesh.setRotationFromEuler(obj.rotation);//set rotation
                            glassMesh.applyMatrix4(m)//scale

                            frameMesh.name = "window"
                            frameMesh.position.set(position.x, position.y, position.z)//set the original position
                            frameMesh.setRotationFromEuler(obj.rotation);
                            frameMesh.applyMatrix4(m)//scale

                            new_scene.add(frameMesh)
                            new_scene.add(glassMesh)
                            break;
                        }
                    }
                } else if (this.children[index].__itemModel.itemName === "Parametric Door") {
                    //console.log(this.children[index]);
                    if(child.floor != undefined){
                        let floorClone = child.floor.clone();
                        floorClone.name='floor';
                        floorClone.material.name = "Eiche rustikal weiss";
                        floorClone.setRotationFromEuler(child.children[1].rotation);
                        floorClone.position.set(position.x, position.y, position.z)//set the original position
                        floorClone.applyMatrix4(m)//scale
                        new_scene.add(floorClone);
                    }
                    let name = "door";
                    for (let obj of child.children) {
                        if (obj.type === "Mesh") {//find the right one
                            let mat = new MeshBasicMaterial();
                            for (let index = 0; index < obj.material.length; index++) {
                                //the door has multiple material that causes problem afterward, so only one material is kept
                                if (obj.material[index].name === "Legno_A") {
                                    mat = obj.material[index].clone()
                                } 
                            }
                            real_mesh = new Mesh(obj.geometry.clone(), mat);
                            real_mesh.name = name
                            real_mesh.setRotationFromEuler(obj.rotation);
                            real_mesh.position.set(position.x, position.y, position.z)//set the original position
                            real_mesh.applyMatrix4(m)//scale
                            new_scene.add(real_mesh)
                            break;
                        }
                    }
                } else {//for now only a single object can be exported: DoubleChairWithPillow
                    for (let obj of child.children) {
                        if (obj.type === "Group") {
                            // name of the object
                            let name = child.__itemModel.__metadata;
                            let group = new Group()//is exported as a group of meshes
                            group.name = name;
                            //group.name = "chair"
                            obj.children[0].children.forEach(mesh => {
                                real_mesh = mesh.clone()
                                real_mesh.setRotationFromEuler(obj.rotation);
                                real_mesh.position.set(position.x, position.y, position.z)//set the original position
                                real_mesh.applyMatrix4(m)//scale
                                group.add(real_mesh)
                            })
                            // for some reason the ping pong table is a mesh
                            // without other children.
                            if(obj.children[0].children.length == 0){
                                let copiedMesh = obj.children[0].clone();
                                //copiedMesh.setRotationFromEuler(obj.rotation);
                                copiedMesh.position.set(position.x, position.y, position.z)//set the original position
                                copiedMesh.applyMatrix4(m)//scale
                                group.add(copiedMesh)
                            }
                            new_scene.add(group)
                            break;
                        }
                    }
                }
            }
            
        }
        if (returnScene) {
            scope.dispatchEvent({type: "SCENE_EXPORTED", scene: new_scene, wallHeight : this.model.floorplan.walls[0].height, sendToHegias: sendToHegias, orgId: orgId, projectId: projectId})
        }else{
            console.log(new_scene);
            exporter.parse(new_scene, function(gltf) {
                scope.dispatchEvent({ type: EVENT_GLTF_READY, gltf: gltf, sendToHegias: sendToHegias, orgId: orgId, projectId: projectId });
            }, {onlyVisible:false, binary:true});
        }
    }

    // separate a window in ywo different Mesh, one for the frame and one for the glass.
    // oldWindow is the original Mesh (position, rotation and scale are not set in this function! But after the value is returned!!)
    createNewWindow(oldWindow) {
        let glassMaterialIndex; //index of the glass material in the oldwindow group
        let glassMaterial; // material for the glass
        let frameMaterial; //material for the windoe frame

        let glassVertexBuffer = []; //buffer for vertex of the BufferGeometry for the glas Mesh
        let glassNormalBuffer = []; //buffer for normals
        let glassColorBuffer = []; //buffer for colors

        //these array won't be modified, they contain a copy of the oldWindow
        let frameVertexBuffer; 
        let frameNormalBuffer; 
        let frameColorBuffer; 

        //these buffer will contain only the elements for the windows frame
        let newFrameVertexBuffer = []; //buffer for vertex of the BufferGeometry for the window frame
        let newFrameNormalBuffer = []; //buffer for normal
        let newFrameColorBuffer = []; //buffer for colors

        for (let [index, material] of oldWindow.material.entries()) {// search the materials and store them for later use
            if (material.name === "glass") {
                glassMaterialIndex = index;// the index is necessary to distinguish later between glass elements and window frame elements (vertex, normal, color)
                glassMaterial = material.clone()
            } else if (material.name === "Legno_A") {
                frameMaterial = material.clone() //material for the window frame
            }
        }
        //copy attribute as array so they can be manipulated with array functions
        frameVertexBuffer = Array.from(oldWindow.geometry.getAttribute("position").clone().array)
        frameNormalBuffer = Array.from(oldWindow.geometry.getAttribute("normal").clone().array)
        frameColorBuffer = Array.from(oldWindow.geometry.getAttribute("color").clone().array)

        //separate elements into glass and frame
        oldWindow.geometry.groups.forEach( group => {
            let start = group.start * 3;                    //start of the vertices belonging to this group
            let materialIndex = group.materialIndex;    //index of the material (we are looking for the one that is glass)
            let count = group.count * 3;                    //number of vertices belonging to the group
            // the multiplication by 3 is needed since a vertex is composed by 3 element (x, y and z), (normal and colors also have 3 elements)
            if (materialIndex === glassMaterialIndex) { //if the index is the one from glass, then the element belong in the glass buffer
                //copy the element from the frame buffers and add them to the glass buffers
                glassVertexBuffer = glassVertexBuffer.concat(frameVertexBuffer.slice(start, start + count));
                glassNormalBuffer = glassNormalBuffer.concat(frameNormalBuffer.slice(start, start + count));
                glassColorBuffer = glassColorBuffer.concat(frameColorBuffer.slice(start, start + count));
            } else {
                //copy the vertices from the window frame and add to the new frame buffer
                newFrameVertexBuffer = newFrameVertexBuffer.concat(frameVertexBuffer.slice(start, start + count));
                newFrameNormalBuffer = newFrameNormalBuffer.concat(frameNormalBuffer.slice(start, start + count));
                newFrameColorBuffer = newFrameColorBuffer.concat(frameColorBuffer.slice(start, start + count));
            }
        })
        
        const glassGeometry = new BufferGeometry() //create the geometry for the glass and set the attributes
        glassGeometry.setAttribute('position', new BufferAttribute( new Float32Array(glassVertexBuffer), 3));
        glassGeometry.setAttribute('normal', new BufferAttribute( new Float32Array(glassNormalBuffer), 3));
        glassGeometry.setAttribute('color', new BufferAttribute( new Float32Array(glassColorBuffer), 3));

        const frameGeometry = new BufferGeometry() // geometry for the window frame
        frameGeometry.setAttribute('position', new BufferAttribute( new Float32Array(newFrameVertexBuffer), 3));
        frameGeometry.setAttribute('normal', new BufferAttribute( new Float32Array(newFrameNormalBuffer), 3));
        frameGeometry.setAttribute('color', new BufferAttribute( new Float32Array(newFrameColorBuffer), 3));
        
        let glassMesh = new Mesh(glassGeometry, glassMaterial);//create the two meshes
        let frameMesh = new Mesh(frameGeometry, frameMaterial);

        return {glassMesh:glassMesh, frameMesh:frameMesh}; // these meshes still need to have the position, rotation and scale fixed!!!!
    }

    forceRender() {
        let scope = this;
        scope.renderer.render(scope, scope.camera);
        scope.lastRender = Date.now();
    }

    addRoomplanListener(type, listener) {
        this.addEventListener(type, listener);
    }

    removeRoomplanListener(type, listener) {
        this.removeEventListener(type, listener);
    }

    get environmentCamera() {
        return this.__environmentCamera;
    }

    get physicalRoomItems() {
        return this.__physicalRoomItems;
    }

    get enabled() {
        return this.__enabled;
    }

    set enabled(flag) {
        this.__enabled = flag;
        this.controls.enabled = flag;
        if (!flag) {
            this.dragcontrols.deactivate();
        } else {
            this.dragcontrols.activate();
        }
    }

}